import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import VetExamOverlay from "components/VetExamOverlay";
import VetExamsTable from "components/VetExamsTable";
import DataAbsenceView from "components/VetExamsTable/DataAbsenceView";
import { IExamsTableRowData } from "components/VetExamsTable/types";
import { useRiskFactorsColumnWidth } from "components/VetExamsTable/useRiskFactorsColumnWidth";
import { useApiClient } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import { setIsExamFormOpen } from "store/actions/vetExam";
import { resetCheckedIds } from "store/actions/vetWorkoutsPage";
import {
  WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY,
  WorkoutsTableTab,
  checkCurrentWorkoutsTableTab
} from "../helper";
import WorkoutRow from "./WorkoutRow";
import { createConfigTable } from "./WorkoutRow/helper";
import WorkoutsTableHeader from "./WorkoutsTableHeader";
import { EColumnName } from "./options";

import useStyles from "./styles";

interface IProps {
  rows: IExamsTableRowData[];
  isFetchingNextPage: boolean;
  isRefetching?: boolean;
  tableBodyRef: React.RefObject<HTMLDivElement>;
  searchValue: string;
  onClearSearch: (value: boolean) => void;
}

const WorkoutsTable = (props: IProps) => {
  const {
    rows,
    isFetchingNextPage,
    isRefetching,
    tableBodyRef,
    searchValue,
    onClearSearch
  } = props;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { currentUser } = useLoggedInUser();
  const { horseExamClient } = useApiClient();
  const { showErrorSnack } = useSnackbar();

  const [activeExamId, setActiveExamId] = useState<string>();

  const { selectedTab, orderBy, isExamFormOpen } = useAppSelector(state => {
    const { selectedTab, orderBy } = state.vetWorkoutsPage;
    const isExamFormOpen = state.vetExam.isExamFormOpen;

    return {
      selectedTab,
      orderBy,
      isExamFormOpen
    };
  });

  const { isRequestsTab, isExamsTab, isPassedTab, isFailedTab, isRejectedTab } =
    checkCurrentWorkoutsTableTab(selectedTab.value);

  const columns = useMemo(() => createConfigTable(selectedTab), [selectedTab]);

  const riskFactorsColumnWidth = useRiskFactorsColumnWidth(
    rows,
    isRequestsTab || isExamsTab
  );
  const classes = useStyles({ riskFactorsColumnWidth });

  const extraColumnsClassName = clsx({
    [classes.rowContentRequests]: isRequestsTab,
    [classes.rowContentExams]: isExamsTab,
    [classes.rowContentPassed]: isPassedTab,
    [classes.rowContentFailed]: isFailedTab,
    [classes.rowContentRejected]: isRejectedTab
  });

  const shouldRenderTrainerAccordions =
    orderBy === EColumnName.TRAINER_LASTNAME;

  const handleClearSearch = useCallback(() => {
    onClearSearch(true);
  }, [onClearSearch]);

  const refetchData = useCallback(async (): Promise<void> => {
    queryClient.invalidateQueries({
      queryKey: [
        `${currentUser.rh360Id}-${searchValue}-infinite-workout-requests`
      ]
    });
    queryClient.invalidateQueries({
      queryKey: [WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY]
    });
  }, [currentUser.rh360Id, searchValue]);

  const handleExamClick = useCallback(
    (rowData: IExamsTableRowData) => {
      const { examId } = rowData;

      horseExamClient
        .listHorseExams({
          query: {
            ids: [examId]
          },
          getOptions: {
            select: ["status"]
          }
        })
        .then(result => {
          const isExamAvailable = [
            racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_REQUESTED,
            racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_IN_PROGRESS,
            racehorse360.HorseExamStatus.HORSE_EXAM_STATUS_PENDING
          ].includes(result.horseExams[0].status);

          if (isExamAvailable) {
            setActiveExamId(examId);

            dispatch(resetCheckedIds());
            dispatch(setIsExamFormOpen(true));
          } else {
            showErrorSnack("This exam has already been completed");
            return refetchData();
          }
        })
        .catch(error => {
          console.error(error);
        });
    },
    [dispatch]
  );

  const handleExamClose = () => {
    dispatch(setIsExamFormOpen(false));
    setActiveExamId(null);
  };

  const renderRow = (rowData: IExamsTableRowData, index: number) => {
    return (
      <WorkoutRow
        index={index}
        currentRowData={rowData}
        columns={columns}
        columnsClassName={extraColumnsClassName}
        onExamClick={handleExamClick}
      />
    );
  };

  const noWorkoutsContent = useMemo(() => {
    let tabName: string;

    switch (selectedTab.value) {
      case WorkoutsTableTab.REQUESTS: {
        tabName = "Workout Requests";
        break;
      }
      case WorkoutsTableTab.EXAMS: {
        tabName = "Exams";
        break;
      }
      default: {
        tabName = `${selectedTab.name} Exams`;
      }
    }

    return (
      <DataAbsenceView
        isSearchResults={Boolean(searchValue?.length)}
        onClearSearch={handleClearSearch}
        searchValue={searchValue}
        message={`No ${tabName} to Display for Date Range`}
      />
    );
  }, [selectedTab.value, searchValue]);

  const tableHeaderContent = useMemo(
    () => (
      <WorkoutsTableHeader
        columnsClassName={extraColumnsClassName}
        columns={columns}
        refElement={tableBodyRef?.current}
      />
    ),
    [columns, extraColumnsClassName, tableBodyRef?.current]
  );

  if (isExamFormOpen && activeExamId) {
    return (
      <VetExamOverlay
        examId={activeExamId}
        refetchData={refetchData}
        onClose={handleExamClose}
      />
    );
  }

  return (
    <VetExamsTable
      rows={rows}
      isMappedByTrainer={shouldRenderTrainerAccordions}
      isFetchingNextPage={isFetchingNextPage}
      isRefetching={isRefetching}
      tableBodyRef={tableBodyRef}
      noResultsContent={noWorkoutsContent}
      tableHeaderContent={tableHeaderContent}
      refetchData={refetchData}
      renderRow={renderRow}
      hasTrainerAccordionViewFixedCells={isExamsTab}
      shouldUseLockableRow={isExamsTab}
    />
  );
};

export default React.memo(WorkoutsTable);
